import { useMemo } from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import {
  Car,
  FileText,
  CreditCard,
  Bike,
  Bell,
  Clock,
  ImageOff,
  ArrowRight,
  BarChart3,
  MessageSquare,
} from "lucide-react";
import {
  PieChart,
  Pie,
  Cell,
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
  Label,
} from "recharts";
import { getVehicleCount, getVehiclesByTypeBreakdown, getTopVehicles } from "@/services/vehicleService";
import { getUserCount } from "@/services/userService";
import { getPendingFinancingCount, getFinancingByStatus, getRecentFinancingRequests } from "@/services/financingRequestService";
import { getQuoteCount, getQuotesByMonth, getRecentQuotes } from "@/services/quoteService";
import { getRecentQuoteRequests } from "@/services/quoteRequestService";
import { queryKeys } from "@/lib/queryKeys";

/* ------------------------------------------------------------------ */
/*  Helpers                                                           */
/* ------------------------------------------------------------------ */

const FINANCING_STATUS_COLORS: Record<string, string> = {
  pendiente: "#F59E0B",
  en_revision: "#3B82F6",
  aprobado: "#10B981",
  rechazado: "#EF4444",
  completado: "#8B5CF6",
  cancelado: "#6B7280",
};

const FINANCING_STATUS_LABELS: Record<string, string> = {
  pendiente: "Pendiente",
  en_revision: "En revisión",
  aprobado: "Aprobado",
  rechazado: "Rechazado",
  completado: "Completado",
  cancelado: "Cancelado",
};

const MONTH_LABELS: Record<string, string> = {
  "01": "Ene",
  "02": "Feb",
  "03": "Mar",
  "04": "Abr",
  "05": "May",
  "06": "Jun",
  "07": "Jul",
  "08": "Ago",
  "09": "Sep",
  "10": "Oct",
  "11": "Nov",
  "12": "Dic",
};

function formatCurrency(value: number): string {
  return `RD$ ${new Intl.NumberFormat("es-DO").format(value)}`;
}

function toDate(ts: unknown): Date | null {
  if (!ts) return null;
  if (ts instanceof Date) return ts;
  if (typeof ts === "object" && ts !== null && "toDate" in ts && typeof (ts as { toDate: () => Date }).toDate === "function") {
    return (ts as { toDate: () => Date }).toDate();
  }
  if (typeof ts === "string" || typeof ts === "number") {
    const d = new Date(ts);
    return isNaN(d.getTime()) ? null : d;
  }
  return null;
}

function relativeTime(date: Date): string {
  const now = Date.now();
  const diff = now - date.getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return "Hace un momento";
  if (mins < 60) return `Hace ${mins} min`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `Hace ${hours} hora${hours > 1 ? "s" : ""}`;
  const days = Math.floor(hours / 24);
  if (days < 30) return `Hace ${days} día${days > 1 ? "s" : ""}`;
  const months = Math.floor(days / 30);
  return `Hace ${months} mes${months > 1 ? "es" : ""}`;
}

function formatMonthLabel(month: string): string {
  const parts = month.split("-");
  const mm = parts.length > 1 ? parts[1] : parts[0];
  return MONTH_LABELS[mm] || month;
}

/* ------------------------------------------------------------------ */
/*  Skeleton helpers                                                   */
/* ------------------------------------------------------------------ */

function KpiSkeleton() {
  return (
    <Card className="rounded-2xl shadow-sm">
      <CardContent className="p-6">
        <div className="flex items-center gap-4">
          <div className="h-1 w-1 shrink-0 rounded-full bg-gradient-to-b from-zinc-200 to-zinc-100 animate-pulse" style={{ width: 4, minHeight: 48 }} />
          <div className="space-y-2 flex-1">
            <div className="h-8 w-20 animate-pulse rounded-2xl bg-gradient-to-r from-zinc-200 via-zinc-100 to-zinc-200" />
            <div className="h-4 w-28 animate-pulse rounded-2xl bg-gradient-to-r from-zinc-200 via-zinc-100 to-zinc-200" />
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

function ChartSkeleton() {
  return (
    <Card className="rounded-2xl shadow-sm">
      <CardContent className="p-6">
        <div className="h-4 w-32 animate-pulse rounded-2xl bg-gradient-to-r from-zinc-200 via-zinc-100 to-zinc-200 mb-6" />
        <div className="h-[280px] w-full animate-pulse rounded-2xl bg-gradient-to-r from-zinc-100 via-zinc-50 to-zinc-100" />
      </CardContent>
    </Card>
  );
}

function ListSkeleton({ rows = 4 }: { rows?: number }) {
  return (
    <div className="space-y-0">
      {Array.from({ length: rows }).map((_, i) => (
        <div key={i} className={`flex items-center gap-3 px-4 py-3 ${i % 2 === 1 ? "bg-muted/30" : ""}`}>
          <div className="h-8 w-8 animate-pulse rounded-xl bg-gradient-to-r from-zinc-200 via-zinc-100 to-zinc-200" />
          <div className="h-4 flex-1 animate-pulse rounded-2xl bg-gradient-to-r from-zinc-200 via-zinc-100 to-zinc-200" />
          <div className="h-5 w-16 animate-pulse rounded-full bg-gradient-to-r from-zinc-200 via-zinc-100 to-zinc-200" />
        </div>
      ))}
    </div>
  );
}

/* ------------------------------------------------------------------ */
/*  Custom center label for donut chart                                */
/* ------------------------------------------------------------------ */

function DonutCenterLabel({ viewBox, total }: { viewBox?: { cx?: number; cy?: number }; total: number }) {
  const cx = viewBox?.cx ?? 0;
  const cy = viewBox?.cy ?? 0;
  return (
    <g>
      <text x={cx} y={cy - 6} textAnchor="middle" className="fill-foreground text-3xl font-bold">
        {total}
      </text>
      <text x={cx} y={cy + 14} textAnchor="middle" className="fill-muted-foreground text-xs">
        Total
      </text>
    </g>
  );
}

/* ------------------------------------------------------------------ */
/*  Main Component                                                     */
/* ------------------------------------------------------------------ */

export function Panel() {
  /* ---------- queries ---------- */
  const { data: vehicleCount, isLoading: loadingVehicles } = useQuery({
    queryKey: queryKeys.vehicleCount,
    queryFn: getVehicleCount,
  });
  const { data: quoteCount, isLoading: loadingQuotes } = useQuery({
    queryKey: queryKeys.quoteCount,
    queryFn: getQuoteCount,
  });
  const { data: pendingFinancingCount, isLoading: loadingPending } = useQuery({
    queryKey: queryKeys.pendingFinancingCount,
    queryFn: getPendingFinancingCount,
  });
  const { data: userCount, isLoading: loadingUsers } = useQuery({
    queryKey: queryKeys.userCount,
    queryFn: getUserCount,
  });
  const { data: financingByStatus = [], isLoading: loadingFinStatus } = useQuery({
    queryKey: queryKeys.financingByStatus,
    queryFn: getFinancingByStatus,
  });
  const { data: quotesByMonth = [], isLoading: loadingQuotesMonth } = useQuery({
    queryKey: queryKeys.quotesByMonth,
    queryFn: getQuotesByMonth,
  });
  const { data: vehiclesByType = [], isLoading: loadingVType } = useQuery({
    queryKey: queryKeys.vehiclesByType,
    queryFn: getVehiclesByTypeBreakdown,
  });
  const { data: recentFinancings = [], isLoading: loadingRecFin } = useQuery({
    queryKey: queryKeys.recentFinancings,
    queryFn: () => getRecentFinancingRequests(8),
  });
  const { data: recentQuotes = [], isLoading: loadingRecQuotes } = useQuery({
    queryKey: queryKeys.recentQuotes,
    queryFn: () => getRecentQuotes(8),
  });
  const { data: recentQuoteRequests = [], isLoading: loadingRecReqs } = useQuery({
    queryKey: queryKeys.recentQuoteRequests,
    queryFn: () => getRecentQuoteRequests(8),
  });
  const { data: topVehicles = [], isLoading: loadingTopVehicles } = useQuery({
    queryKey: queryKeys.topVehicles,
    queryFn: () => getTopVehicles(8),
  });

  /* ---------- derived ---------- */

  const motosCount = useMemo(
    () => vehiclesByType.find((v) => v.type === "Motocicleta")?.count ?? 0,
    [vehiclesByType],
  );
  const carrosCount = useMemo(
    () => vehiclesByType.find((v) => v.type === "Automóvil" || v.type === "Automovil")?.count ?? 0,
    [vehiclesByType],
  );

  const barData = useMemo(
    () =>
      quotesByMonth.map((item) => ({
        ...item,
        label: formatMonthLabel(item.month),
      })),
    [quotesByMonth],
  );

  const financingTotal = useMemo(
    () => financingByStatus.reduce((sum, entry) => sum + entry.count, 0),
    [financingByStatus],
  );

  // Merge recent activity
  const recentActivity = useMemo(() => {
    type ActivityItem = {
      id: string;
      kind: "financing" | "quote" | "request";
      description: string;
      status?: string;
      date: Date;
    };

    const items: ActivityItem[] = [];

    (recentFinancings as unknown as Record<string, unknown>[]).forEach((f) => {
      const d = toDate(f.createdAt);
      if (d) {
        items.push({
          id: (f.id as string) ?? String(Math.random()),
          kind: "financing",
          description: `Solicitud de financiamiento — ${(f.clientName as string) || (f.name as string) || "Cliente"}`,
          status: f.status as string | undefined,
          date: d,
        });
      }
    });

    (recentQuotes as Record<string, unknown>[]).forEach((q) => {
      const d = toDate(q.createdAt);
      if (d) {
        items.push({
          id: (q.id as string) ?? String(Math.random()),
          kind: "quote",
          description: `Cotización creada — ${(q.clientName as string) || (q.vehicleName as string) || "Vehículo"}`,
          status: q.status as string | undefined,
          date: d,
        });
      }
    });

    (recentQuoteRequests as Record<string, unknown>[]).forEach((r) => {
      const d = toDate(r.createdAt);
      if (d) {
        items.push({
          id: (r.id as string) ?? String(Math.random()),
          kind: "request",
          description: `Solicitud de cotización — ${(r.clientName as string) || (r.name as string) || "Cliente"}`,
          status: r.status as string | undefined,
          date: d,
        });
      }
    });

    items.sort((a, b) => b.date.getTime() - a.date.getTime());
    return items.slice(0, 8);
  }, [recentFinancings, recentQuotes, recentQuoteRequests]);

  const loadingActivity = loadingRecFin || loadingRecQuotes || loadingRecReqs;

  /* ---------- KPI config ---------- */
  const kpis = [
    {
      value: vehicleCount ?? 0,
      label: "Vehículos",
      description: "En inventario",
      loading: loadingVehicles,
      gradient: "from-blue-500 to-blue-600",
      textColor: "text-blue-600",
    },
    {
      value: quoteCount ?? 0,
      label: "Cotizaciones",
      description: "Total creadas",
      loading: loadingQuotes,
      gradient: "from-emerald-500 to-emerald-600",
      textColor: "text-emerald-600",
    },
    {
      value: pendingFinancingCount ?? 0,
      label: "Financiamientos",
      description: "Pendientes",
      loading: loadingPending,
      gradient: "from-amber-500 to-amber-600",
      textColor: "text-amber-600",
    },
    {
      value: userCount ?? 0,
      label: "Usuarios",
      description: "Registrados",
      loading: loadingUsers,
      gradient: "from-violet-500 to-violet-600",
      textColor: "text-violet-600",
    },
  ];

  const kindIcon: Record<string, typeof CreditCard> = {
    financing: CreditCard,
    quote: FileText,
    request: MessageSquare,
  };

  const kindIconColor: Record<string, string> = {
    financing: "text-blue-500 bg-blue-50",
    quote: "text-emerald-500 bg-emerald-50",
    request: "text-amber-500 bg-amber-50",
  };

  /* ---------- Quick actions config ---------- */
  const quickActions = [
    { icon: Car, label: "Nuevo vehículo", to: "/vehicles/new", color: "text-blue-500 bg-blue-50" },
    { icon: FileText, label: "Nueva cotización", to: "/quotes/new", color: "text-emerald-500 bg-emerald-50" },
    { icon: CreditCard, label: "Ver financiamientos", to: "/financings", color: "text-amber-500 bg-amber-50" },
    { icon: Bell, label: "Enviar notificación", to: "/notifications", color: "text-violet-500 bg-violet-50" },
  ];

  /* ================================================================ */
  /*  RENDER                                                          */
  /* ================================================================ */

  return (
    <div className="space-y-6 bg-background">
      {/* Header */}
      <div>
        <h1 className="font-display text-2xl font-semibold text-foreground">Panel</h1>
        <p className="text-muted-foreground">Resumen general de tu negocio</p>
      </div>

      {/* ── Section 1: KPI Cards ── */}
      <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
        {kpis.map((kpi, i) => {
          if (kpi.loading) return <KpiSkeleton key={i} />;
          return (
            <Card
              key={i}
              className="group relative rounded-2xl shadow-sm overflow-hidden transition-shadow duration-300 hover:shadow-md"
            >
              {/* Gradient accent stripe */}
              <div
                className={`absolute left-0 top-0 bottom-0 w-1 rounded-l-2xl bg-gradient-to-b ${kpi.gradient}`}
              />
              {/* Subtle hover overlay */}
              <div className="absolute inset-0 bg-gradient-to-br from-transparent to-transparent group-hover:from-black/[0.01] group-hover:to-black/[0.03] transition-all duration-300 pointer-events-none" />
              <CardContent className="p-6 pl-5">
                <p className="text-3xl font-bold tracking-tight tabular-nums text-foreground">
                  {kpi.value}
                </p>
                <p className="mt-1 text-sm font-medium text-muted-foreground">{kpi.label}</p>
                <p className="text-xs text-muted-foreground/70">{kpi.description}</p>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* ── Section 2: Charts ── */}
      <div className="grid gap-6 lg:grid-cols-2">
        {/* Donut: Financiamientos por estado */}
        {loadingFinStatus ? (
          <ChartSkeleton />
        ) : (
          <Card className="rounded-2xl shadow-sm">
            <CardHeader className="pb-2">
              <p className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
                Financiamientos por estado
              </p>
            </CardHeader>
            <CardContent className="pb-6">
              {financingByStatus.length === 0 ? (
                <div className="flex h-[280px] items-center justify-center text-sm text-muted-foreground">
                  Sin datos
                </div>
              ) : (
                <>
                  <ResponsiveContainer width="100%" height={280}>
                    <PieChart>
                      <Pie
                        data={financingByStatus}
                        dataKey="count"
                        nameKey="status"
                        cx="50%"
                        cy="50%"
                        innerRadius={68}
                        outerRadius={105}
                        paddingAngle={3}
                        strokeWidth={0}
                      >
                        {financingByStatus.map((entry) => (
                          <Cell
                            key={entry.status}
                            fill={FINANCING_STATUS_COLORS[entry.status] ?? "#9CA3AF"}
                          />
                        ))}
                        <Label
                          content={<DonutCenterLabel total={financingTotal} />}
                          position="center"
                        />
                      </Pie>
                      <Tooltip
                        formatter={(value: unknown, name: unknown) => [
                          String(value),
                          FINANCING_STATUS_LABELS[name as string] ?? String(name),
                        ]}
                        contentStyle={{
                          borderRadius: 12,
                          border: "none",
                          boxShadow: "0 4px 12px rgba(0,0,0,0.08)",
                        }}
                      />
                    </PieChart>
                  </ResponsiveContainer>
                  <div className="mt-2 flex flex-wrap justify-center gap-x-4 gap-y-1">
                    {financingByStatus.map((entry) => (
                      <div key={entry.status} className="flex items-center gap-1.5 text-xs text-muted-foreground">
                        <span
                          className="inline-block h-2.5 w-2.5 rounded-full"
                          style={{ backgroundColor: FINANCING_STATUS_COLORS[entry.status] ?? "#9CA3AF" }}
                        />
                        {FINANCING_STATUS_LABELS[entry.status] ?? entry.status} ({entry.count})
                      </div>
                    ))}
                  </div>
                </>
              )}
            </CardContent>
          </Card>
        )}

        {/* Bar: Cotizaciones por mes */}
        {loadingQuotesMonth ? (
          <ChartSkeleton />
        ) : (
          <Card className="rounded-2xl shadow-sm">
            <CardHeader className="pb-2">
              <p className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
                Cotizaciones por mes
              </p>
            </CardHeader>
            <CardContent className="pb-6">
              {barData.length === 0 ? (
                <div className="flex h-[280px] items-center justify-center text-sm text-muted-foreground">
                  Sin datos
                </div>
              ) : (
                <ResponsiveContainer width="100%" height={280}>
                  <BarChart data={barData} margin={{ top: 8, right: 8, left: -12, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" vertical={false} horizontal={false} />
                    <XAxis
                      dataKey="label"
                      tick={{ fontSize: 12 }}
                      axisLine={false}
                      tickLine={false}
                    />
                    <YAxis
                      allowDecimals={false}
                      tick={{ fontSize: 12 }}
                      axisLine={false}
                      tickLine={false}
                    />
                    <Tooltip
                      formatter={(value: unknown) => [String(value), "Cotizaciones"]}
                      contentStyle={{
                        borderRadius: 12,
                        border: "none",
                        boxShadow: "0 4px 12px rgba(0,0,0,0.08)",
                      }}
                    />
                    <defs>
                      <linearGradient id="barGradient" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor="#3B82F6" stopOpacity={1} />
                        <stop offset="100%" stopColor="#60A5FA" stopOpacity={0.8} />
                      </linearGradient>
                    </defs>
                    <Bar dataKey="count" fill="url(#barGradient)" radius={[6, 6, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              )}
            </CardContent>
          </Card>
        )}
      </div>

      {/* ── Vehicle type breakdown: Glass cards ── */}
      {loadingVType ? (
        <div className="grid gap-6 sm:grid-cols-2">
          {[0, 1].map((i) => (
            <Card key={i} className="rounded-2xl shadow-sm">
              <CardContent className="p-6">
                <div className="h-16 animate-pulse rounded-2xl bg-gradient-to-r from-zinc-200 via-zinc-100 to-zinc-200" />
              </CardContent>
            </Card>
          ))}
        </div>
      ) : (
        <div className="grid gap-6 sm:grid-cols-2">
          <Card className="rounded-2xl shadow-sm overflow-hidden relative">
            <div className="absolute inset-0 bg-gradient-to-br from-orange-50/80 to-amber-50/40 pointer-events-none" />
            <CardContent className="flex items-center gap-5 p-6 relative">
              <div className="flex h-16 w-16 shrink-0 items-center justify-center rounded-2xl bg-white/70 backdrop-blur-sm shadow-sm">
                <Bike className="h-8 w-8 text-orange-500" />
              </div>
              <div>
                <p className="text-3xl font-bold tabular-nums text-foreground">{motosCount}</p>
                <p className="text-sm font-medium text-muted-foreground">Motos en inventario</p>
              </div>
            </CardContent>
          </Card>
          <Card className="rounded-2xl shadow-sm overflow-hidden relative">
            <div className="absolute inset-0 bg-gradient-to-br from-blue-50/80 to-indigo-50/40 pointer-events-none" />
            <CardContent className="flex items-center gap-5 p-6 relative">
              <div className="flex h-16 w-16 shrink-0 items-center justify-center rounded-2xl bg-white/70 backdrop-blur-sm shadow-sm">
                <Car className="h-8 w-8 text-blue-500" />
              </div>
              <div>
                <p className="text-3xl font-bold tabular-nums text-foreground">{carrosCount}</p>
                <p className="text-sm font-medium text-muted-foreground">Carros en inventario</p>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* ── Section 3: Recent Activity ── */}
      <Card className="rounded-2xl shadow-sm">
        <CardHeader className="pb-2">
          <p className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
            Actividad reciente
          </p>
        </CardHeader>
        <CardContent className="p-0">
          {loadingActivity ? (
            <div className="p-4">
              <ListSkeleton rows={6} />
            </div>
          ) : recentActivity.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-10 text-muted-foreground">
              <Clock className="mb-2 h-8 w-8 opacity-40" />
              <p className="text-sm">No hay actividad reciente</p>
            </div>
          ) : (
            <div>
              {recentActivity.map((item, idx) => {
                const IconComp = kindIcon[item.kind] ?? FileText;
                const iconClasses = kindIconColor[item.kind] ?? "text-zinc-500 bg-zinc-50";
                return (
                  <div key={item.id}>
                    <div
                      className={`flex items-center gap-3 px-5 py-3 transition-colors ${
                        idx % 2 === 1 ? "bg-muted/30" : ""
                      }`}
                    >
                      {/* Colored icon */}
                      <div className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-lg ${iconClasses}`}>
                        <IconComp className="h-4 w-4" />
                      </div>
                      {/* Description + time */}
                      <div className="min-w-0 flex-1">
                        <p className="truncate text-sm text-foreground">{item.description}</p>
                        <p className="text-xs text-muted-foreground">{relativeTime(item.date)}</p>
                      </div>
                      {/* Status badge */}
                      {item.status && (
                        <span
                          className="shrink-0 inline-flex items-center rounded-full px-2.5 py-0.5 text-[11px] font-medium"
                          style={{
                            backgroundColor: `${FINANCING_STATUS_COLORS[item.status] ?? "#9CA3AF"}15`,
                            color: FINANCING_STATUS_COLORS[item.status] ?? "#6B7280",
                          }}
                        >
                          {FINANCING_STATUS_LABELS[item.status] ?? item.status}
                        </span>
                      )}
                    </div>
                    {/* Separator line between items */}
                    {idx < recentActivity.length - 1 && (
                      <div className="mx-5 border-b border-border/40" />
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      {/* ── Section 4: Top Vehicles ── */}
      <Card className="rounded-2xl shadow-sm">
        <CardHeader className="flex flex-row items-center justify-between pb-2">
          <p className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
            Vehículos destacados
          </p>
          <Link
            to="/vehicles/motorcycles"
            className="inline-flex items-center gap-1 text-sm font-medium text-primary hover:underline"
          >
            Ver todos <ArrowRight className="h-3.5 w-3.5" />
          </Link>
        </CardHeader>
        <CardContent>
          {loadingTopVehicles ? (
            <div className="flex gap-4 overflow-hidden">
              {Array.from({ length: 4 }).map((_, i) => (
                <div key={i} className="h-52 w-48 shrink-0 animate-pulse rounded-2xl bg-gradient-to-b from-zinc-200 via-zinc-100 to-zinc-200" />
              ))}
            </div>
          ) : topVehicles.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-10 text-muted-foreground">
              <BarChart3 className="mb-2 h-8 w-8 opacity-40" />
              <p className="text-sm">Sin vehículos destacados</p>
            </div>
          ) : (
            <div className="flex gap-4 overflow-x-auto pb-2">
              {topVehicles.map((vehicle) => {
                const v = vehicle as unknown as Record<string, unknown>;
                const images = (v.images as string[] | undefined) ?? [];
                const thumb = images[0];
                const name = (v.name as string) || "Vehículo";
                const rawBrand = v.brand;
                const brandName = (v.brandName as string) || (typeof rawBrand === "string" ? rawBrand : (rawBrand as Record<string, unknown> | null)?.name as string ?? "");
                const price = (v.salePrice as number) ?? (v.price as number) ?? 0;

                return (
                  <div
                    key={vehicle.id as string}
                    className="w-48 shrink-0 overflow-hidden rounded-xl bg-card shadow-sm transition-all duration-300 hover:-translate-y-0.5 hover:shadow-md cursor-pointer"
                  >
                    {thumb ? (
                      <img
                        src={thumb}
                        alt={name}
                        className="h-32 w-full object-cover"
                        loading="lazy"
                      />
                    ) : (
                      <div className="flex h-32 w-full items-center justify-center bg-muted/50 text-muted-foreground/40">
                        <ImageOff className="h-8 w-8" />
                      </div>
                    )}
                    <div className="p-3">
                      <p className="truncate text-sm font-bold text-foreground">{name}</p>
                      {brandName && (
                        <p className="truncate text-xs text-muted-foreground">{brandName}</p>
                      )}
                      <p className="mt-1 text-sm font-bold text-primary">
                        {formatCurrency(price)}
                      </p>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      {/* ── Section 5: Quick Actions ── */}
      <div>
        <p className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground mb-4">
          Acciones rápidas
        </p>
        <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
          {quickActions.map((action) => {
            const Icon = action.icon;
            return (
              <Link key={action.to} to={action.to}>
                <Card className="group rounded-2xl shadow-sm transition-all duration-300 hover:-translate-y-0.5 hover:shadow-md cursor-pointer">
                  <CardContent className="flex flex-col items-center justify-center gap-3 p-6">
                    <div className={`flex h-12 w-12 items-center justify-center rounded-xl ${action.color} transition-transform duration-300 group-hover:scale-110`}>
                      <Icon className="h-5 w-5" />
                    </div>
                    <p className="text-sm font-medium text-foreground text-center">{action.label}</p>
                  </CardContent>
                </Card>
              </Link>
            );
          })}
        </div>
      </div>
    </div>
  );
}
